import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bell, Star, Send, Save, Loader2, Lock, RefreshCw, Activity, Clock, MessageSquare, Smartphone } from 'lucide-react';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useModules } from '../../context/FeatureContext';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE, SectionCard, Switch, Field, StatusPill, EmptyState, ErrorState, TableSkeleton, fmtDateTime, num,
} from '../../components/ui';

// Reminders & reviews: SMS no-show reminders before a shift, and a Google review request after it.
// The top bar already names the page. Nothing is saved until "Save changes"; the bar at the bottom
// says when there are unsaved edits, and test messages always use the saved text.

const LOG_META = {
  reminder_24h: { label: '24-hour reminder', icon: Bell },
  reminder_2h:  { label: '2-hour reminder', icon: Clock },
  review:       { label: 'Review request', icon: Star },
};
const LOG_STATUS = {
  sent:      { label: 'Sent', tone: 'success' },
  delivered: { label: 'Delivered', tone: 'success' },
  success:   { label: 'Sent', tone: 'success' },
  skipped:   { label: 'Skipped', tone: 'neutral' },
  failed:    { label: 'Failed', tone: 'danger' },
  error:     { label: 'Failed', tone: 'danger' },
};

const EMPTY_FORM = {
  noshow_enabled: false,
  noshow_24h_enabled: false,
  noshow_2h_enabled: false,
  noshow_24h_message: '',
  noshow_2h_message: '',
  review_enabled: false,
  review_google_url: '',
  review_delay_hours: 24,
  review_message: '',
};

// SMS parts: 160 characters in one message (153 per part once it splits); 70 / 67 when the text
// has characters outside the basic SMS alphabet (curly quotes, emoji, most accents).
function smsLength(text) {
  const s = String(text || '');
  const unicode = /[^\x0A\x0D\x20-\x7E£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¡ÄÖÑÜ§¿äöñüà€]/.test(s);
  const single = unicode ? 70 : 160;
  const part = unicode ? 67 : 153;
  const len = s.length;
  const parts = len === 0 ? 0 : len <= single ? 1 : Math.ceil(len / part);
  return { len, limit: parts > 1 ? part * parts : single, parts, unicode };
}

// A message box whose placeholder chips insert at the cursor, with the SMS length underneath.
function MessageBox({ id, value, onChange, disabled, placeholder, tokens, rows = 2 }) {
  const ref = useRef(null);
  const insert = (token) => {
    const el = ref.current;
    const cur = String(value || '');
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      const pos = start + token.length;
      ref.current.setSelectionRange(pos, pos);
    });
  };
  const sms = smsLength(value);
  return (
    <div>
      <textarea id={id} ref={ref} rows={rows} value={value} disabled={disabled} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="input-field resize-y disabled:bg-slate-50 disabled:text-slate-500" />
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <span className="text-[12px] text-slate-500 mr-0.5">Insert</span>
        {tokens.map((t) => (
          <button key={t} type="button" disabled={disabled} onClick={() => insert(t)}
            aria-label={`Insert ${t}`}
            className="h-7 px-2 rounded-md border border-slate-200 bg-white text-[12px] font-mono text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
            {t}
          </button>
        ))}
        <span className="ml-auto text-[12px] tabular-nums text-slate-500" aria-live="polite">
          {num(sms.len)} / {num(sms.limit)} · {sms.parts <= 1 ? '1 SMS' : `${num(sms.parts)} SMS`}{sms.unicode ? ' (special characters)' : ''}
        </span>
      </div>
    </div>
  );
}

// Inline "send a test" row. Tests always use the saved settings.
function TestRow({ label, savedHint, dirty, busy, onSend }) {
  const [phone, setPhone] = useState('');
  const inputId = `test-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
      <label htmlFor={inputId} className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700">
        <Smartphone size={13} className="text-slate-400" /> {label}
      </label>
      <p className={clsx('text-[12px] mt-0.5', dirty ? 'text-amber-700' : 'text-slate-500')}>
        {dirty ? 'You have unsaved edits. Save to test this text; until then the test sends the saved version.' : savedHint}
      </p>
      <div className="flex items-stretch gap-2 mt-2">
        <input id={inputId} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4xx xxx xxx"
          className="input-field flex-1" />
        <button type="button" onClick={() => onSend(phone)} disabled={busy || !phone.trim()} className="btn-secondary shrink-0">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
        </button>
      </div>
    </div>
  );
}

function SubReminder({ title, icon: Icon, enabled, masterOn, onToggle, children }) {
  return (
    <div className={clsx('rounded-xl border p-4', masterOn ? 'border-slate-200' : 'border-slate-100 bg-slate-50/60')}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
          <Icon size={14} className="text-slate-400" /> {title}
        </span>
        {/* With the master switch off the sub-switch shows off and can't be changed; its saved value is kept. */}
        <Switch checked={masterOn && enabled} onChange={onToggle} disabled={!masterOn} label={title} />
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function OrgAutomations() {
  const modules = useModules();
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  // Super admin manages Rostery's global automations config; orgs manage their own.
  const enabledModule = isSuper || modules.automations === true;

  // Role-correct endpoints: super admin → global; orgs → their /my-* endpoints.
  const EP = useMemo(() => ({
    config:       isSuper ? '/automations/config'        : '/automations/my-config',
    stats:        isSuper ? '/automations/stats'         : '/automations/my-stats',
    testReminder: isSuper ? '/automations/test-reminder' : '/automations/my-test-reminder',
    testReview:   isSuper ? '/automations/test-review'   : '/automations/my-test-review',
  }), [isSuper]);

  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // '24h' | '2h' | 'review' | null
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(EMPTY_FORM);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    setErrored(false);
    try {
      const [cfgRes, statsRes] = await Promise.all([
        api.get(EP.config),
        api.get(EP.stats).catch(() => ({ data: {} })),
      ]);
      const d = cfgRes.data || {};
      const next = {
        noshow_enabled: !!d.noshow_enabled,
        noshow_24h_enabled: !!d.noshow_24h_enabled,
        noshow_2h_enabled: !!d.noshow_2h_enabled,
        noshow_24h_message: d.noshow_24h_message || '',
        noshow_2h_message: d.noshow_2h_message || '',
        review_enabled: !!d.review_enabled,
        review_google_url: d.review_google_url || '',
        review_delay_hours: d.review_delay_hours ?? 24,
        review_message: d.review_message || '',
      };
      setForm(next);
      setSaved(next);
      setStats(statsRes.data || {});
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [EP.config, EP.stats]);

  useEffect(() => {
    if (!enabledModule) { setLoading(false); return; }
    load();
  }, [enabledModule, load]);

  const dirty = useMemo(() => Object.keys(EMPTY_FORM).some((k) => String(form[k]) !== String(saved[k])), [form, saved]);

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        noshow_enabled: form.noshow_enabled,
        noshow_24h_enabled: form.noshow_24h_enabled,
        noshow_2h_enabled: form.noshow_2h_enabled,
        noshow_24h_message: form.noshow_24h_message,
        noshow_2h_message: form.noshow_2h_message,
        review_enabled: form.review_enabled,
        review_google_url: form.review_google_url,
        review_delay_hours: Number(form.review_delay_hours) || 0,
        review_message: form.review_message,
      };
      const r = await api.put(EP.config, body);
      toast.success(r.data?.message || 'Reminders and reviews saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save reminders and reviews');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setForm(saved);

  const sendTestReminder = async (phone, type) => {
    if (!phone || !phone.trim()) { toast.error('Enter a phone number first'); return; }
    setTesting(type);
    try {
      const r = await api.post(EP.testReminder, { phone: phone.trim(), type });
      const d = r.data || {};
      if (d.skipped) toast(d.message || 'Test skipped');
      else toast.success(d.preview ? `Sent: ${d.preview}` : (d.message || 'Test reminder sent'), { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send the test reminder');
    } finally {
      setTesting(null);
    }
  };

  const sendTestReview = async (phone) => {
    if (!phone || !phone.trim()) { toast.error('Enter a phone number first'); return; }
    setTesting('review');
    try {
      const r = await api.post(EP.testReview, { phone: phone.trim() });
      const d = r.data || {};
      if (d.skipped) toast(d.message || 'Test skipped');
      else toast.success(d.preview ? `Sent: ${d.preview}` : (d.message || 'Test review request sent'), { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send the test review request');
    } finally {
      setTesting(null);
    }
  };

  /* ── Module not granted ─────────────────────────────────────────────── */
  if (!enabledModule) {
    return (
      <div className={PAGE}>
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState icon={Lock} title="Reminders and reviews aren't turned on"
            hint="They aren't enabled for your organisation. Ask your administrator to enable Google review requests and no-show reminders." />
        </div>
      </div>
    );
  }

  if (loading && !stats) {
    return <div className={PAGE}><div className="bg-white border border-slate-200 rounded-xl"><TableSkeleton rows={8} cols={3} /></div></div>;
  }

  if (errored) {
    return (
      <div className={PAGE}>
        <div className="bg-white border border-slate-200 rounded-xl">
          <ErrorState message="We couldn't load reminders and reviews. Check your connection, then try again." onRetry={load} />
        </div>
      </div>
    );
  }

  const month = stats?.this_month || {};
  const today = stats?.today || {};
  const logs = Array.isArray(stats?.recent_logs) ? stats.recent_logs : [];

  const remindersMonth = month.total_reminders ?? ((month.reminders_24h || 0) + (month.reminders_2h || 0));
  const reviewsMonth = month.reviews ?? 0;
  const todayReminders = (today.reminders_24h || 0) + (today.reminders_2h || 0);
  const todayReviews = today.reviews ?? 0;
  const figures = [
    { label: 'Reminders sent this month', value: remindersMonth },
    { label: 'Review requests this month', value: reviewsMonth },
    { label: 'Reminders today', value: todayReminders },
    { label: 'Review requests today', value: todayReviews },
  ];

  return (
    <div className={PAGE}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] text-slate-500 max-w-2xl">
          Text clients before a shift so fewer are missed, and ask for a Google review after one.
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-secondary" aria-label="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} /> Refresh
          </button>
          <button type="button" onClick={save} disabled={saving || !dirty} className={dirty ? 'btn-primary' : 'btn-secondary'}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl grid grid-cols-2 lg:grid-cols-4">
        {figures.map((f, i) => (
          <div key={f.label} className={clsx('px-5 py-4', i % 2 === 1 && 'border-l border-slate-100', i >= 2 && 'border-t lg:border-t-0 border-slate-100', i === 2 && 'lg:border-l')}>
            <p className="text-[12.5px] text-slate-500">{f.label}</p>
            <p className="mt-1 text-[22px] font-semibold tabular-nums text-slate-900">{num(Number(f.value) || 0)}</p>
          </div>
        ))}
      </div>

      {/* ── No-show reminders ──────────────────────────────────────────── */}
      <SectionCard title="No-show reminders" icon={Bell} bodyClass="p-5 space-y-4"
        actions={<Switch checked={form.noshow_enabled} onChange={(v) => setField('noshow_enabled', v)} label="No-show reminders" />}>
        <p className="text-[13px] text-slate-600">
          Clients get an SMS ahead of an upcoming shift. The placeholders are replaced with the client's name and the shift's
          time and date when each SMS is sent.
        </p>
        <SubReminder title="24-hour reminder" icon={Bell} enabled={form.noshow_24h_enabled} masterOn={form.noshow_enabled}
          onToggle={(v) => setField('noshow_24h_enabled', v)}>
          <MessageBox id="noshow-24h" value={form.noshow_24h_message} onChange={(v) => setField('noshow_24h_message', v)}
            disabled={!form.noshow_enabled} tokens={['{name}', '{time}', '{date}']}
            placeholder="Hi {name}, this is a reminder of your support shift tomorrow at {time} on {date}." />
        </SubReminder>
        <SubReminder title="2-hour reminder" icon={Clock} enabled={form.noshow_2h_enabled} masterOn={form.noshow_enabled}
          onToggle={(v) => setField('noshow_2h_enabled', v)}>
          <MessageBox id="noshow-2h" value={form.noshow_2h_message} onChange={(v) => setField('noshow_2h_message', v)}
            disabled={!form.noshow_enabled} tokens={['{name}', '{time}', '{date}']}
            placeholder="Hi {name}, just a reminder your support shift is at {time} today." />
        </SubReminder>
        <TestRow label="Send a test reminder" dirty={dirty}
          savedHint="Sends your saved 24-hour reminder to this number."
          busy={testing === '24h' || testing === '2h'}
          onSend={(phone) => sendTestReminder(phone, '24h')} />
      </SectionCard>

      {/* ── Google review / client feedback ────────────────────────────── */}
      <SectionCard title="Google review requests" icon={Star} bodyClass="p-5 space-y-4"
        actions={<Switch checked={form.review_enabled} onChange={(v) => setField('review_enabled', v)} label="Google review requests" />}>
        <p className="text-[13px] text-slate-600">
          After a completed shift, clients get an SMS asking them to leave a Google review. The review link replaces {'{url}'} in the message.
        </p>
        <Field label="Google review link" hint="Your Google Business review link. It's sent in place of {url}.">
          {(id) => (
            <input id={id} type="url" value={form.review_google_url} disabled={!form.review_enabled}
              onChange={(e) => setField('review_google_url', e.target.value)}
              placeholder="https://g.page/r/your-business/review" className="input-field disabled:bg-slate-50 disabled:text-slate-500" />
          )}
        </Field>
        <Field label="Wait after the shift (hours)" hint="How long after a shift is completed to send the request.">
          {(id) => (
            <input id={id} type="number" min={0} value={form.review_delay_hours} disabled={!form.review_enabled}
              onChange={(e) => setField('review_delay_hours', e.target.value)} placeholder="24"
              className="input-field tabular-nums sm:!w-40 disabled:bg-slate-50 disabled:text-slate-500" />
          )}
        </Field>
        <Field label="Message">
          {(id) => (
            <MessageBox id={id} rows={3} value={form.review_message} onChange={(v) => setField('review_message', v)}
              disabled={!form.review_enabled} tokens={['{name}', '{url}']}
              placeholder="Hi {name}, thanks for choosing us. We'd love your feedback. Please leave us a review: {url}" />
          )}
        </Field>
        <TestRow label="Send a test review request" dirty={dirty}
          savedHint="Sends your saved review request to this number."
          busy={testing === 'review'} onSend={sendTestReview} />
      </SectionCard>

      {/* ── Recent activity ────────────────────────────────────────────── */}
      <SectionCard title="Recent activity" icon={Activity}>
        {logs.length === 0 ? (
          <EmptyState icon={Activity} title="Nothing sent yet" hint="Reminders and review requests appear here as they go out." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {logs.map((log, i) => {
              const meta = LOG_META[log.type] || { label: log.type || 'Message', icon: MessageSquare };
              const MIcon = meta.icon;
              return (
                <li key={log.id || i} className="flex items-center gap-3 px-5 py-3">
                  <MIcon size={15} className="shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-slate-800">
                      <span className="font-medium">{log.client_name || 'Unknown client'}</span>
                      <span className="text-slate-500"> · {meta.label}{log.phone ? ` · ${log.phone}` : ''}</span>
                    </p>
                    {log.message && <p className="text-[12px] text-slate-500 truncate">{log.message}</p>}
                  </div>
                  <StatusPill status={log.status || 'unknown'} map={LOG_STATUS} />
                  <span className="text-[12px] text-slate-500 whitespace-nowrap hidden sm:block tabular-nums">{log.created_at ? fmtDateTime(log.created_at) : '—'}</span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {dirty && (
        <div className="sticky bottom-20 md:bottom-3 z-20">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-2.5 shadow-card-deep" role="status">
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden /> Unsaved changes
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={discard} disabled={saving} className="btn-secondary btn-sm">Discard</button>
              <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
